import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";
import { getSafeRedirectPath } from "../lib/safeNavigate";
import { isEmergentAuthEnabled } from "../lib/featureFlags";

export default function Login({ admin = false }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [magicSent, setMagicSent] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => { document.title = admin ? "Admin Login | Loomara" : "Sign In | Loomara"; }, [admin]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            if (admin && res.role !== "admin") {
                toast("This account doesn't have admin access.");
                return;
            }
            const fallback = res.role === "admin" ? "/admin" : "/account";
            const redirectTo = getSafeRedirectPath(location.state?.from, fallback);
            toast(`Welcome back${res.role === "admin" ? ", Admin" : ""}.`);
            navigate(redirectTo, { replace: true });
        } else {
            toast(res.error || "Login failed");
        }
    };

    const requestMagic = async () => {
        if (!email) { toast("Enter your email first."); return; }
        try {
            await api.post("/auth/magic-link/request", { email });
            setMagicSent(true);
            toast("Sign-in link sent. Check your inbox ✨");
        } catch (e) {
            toast(e.response?.data?.detail || "Could not send link");
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const googleSignIn = () => {
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center container-boutique py-16" data-testid={admin ? "admin-login-page" : "login-page"}>
            <div className="card-soft p-10 w-full max-w-md">
                <p className="eyebrow text-center">{admin ? "Admin Portal" : "Welcome Back"}</p>
                <h1 className="font-serif text-4xl text-ink-900 text-center mt-2 mb-8">
                    {admin ? "Loomara Admin" : "Sign In"}
                </h1>

                <form onSubmit={submit} className="space-y-4">
                    <input value={email} onChange={(e)=>setEmail(e.target.value)} type="email" required placeholder="Email" className="w-full bg-ivory-100 rounded-xl px-4 py-3 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="login-email" />
                    <input value={password} onChange={(e)=>setPassword(e.target.value)} type="password" required placeholder="Password" className="w-full bg-ivory-100 rounded-xl px-4 py-3 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="login-password" />
                    {!admin && (
                        <p className="text-right">
                            <Link to="/forgot-password" className="text-xs text-sage-700 underline underline-offset-4" data-testid="link-forgot-password">
                                Forgot password?
                            </Link>
                        </p>
                    )}
                    <button disabled={loading} className="btn-primary w-full" data-testid="login-submit">{loading ? "Signing in…" : "Sign In"}</button>
                </form>

                {!admin && (
                    <>
                        <div className="flex items-center gap-3 my-6">
                            <div className="flex-1 h-px bg-sage-300/40" />
                            <span className="text-[10px] uppercase tracking-[0.28em] text-ink-500">or</span>
                            <div className="flex-1 h-px bg-sage-300/40" />
                        </div>

                        <button onClick={requestMagic} className="w-full bg-ivory-100 hover:bg-ivory-50 border border-sage-300/30 text-ink-900 rounded-full px-6 py-3 text-xs uppercase tracking-[0.18em] transition-colors" data-testid="magic-link-btn">
                            {magicSent ? "Link sent — check inbox" : "Email me a sign-in link"}
                        </button>

                        {isEmergentAuthEnabled() && (
                        <button onClick={googleSignIn} className="w-full mt-3 bg-white hover:bg-ivory-50 border border-sage-300/30 text-ink-900 rounded-full px-6 py-3 text-xs uppercase tracking-[0.18em] transition-colors inline-flex items-center justify-center gap-2" data-testid="google-signin-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.49 12c0-.73.13-1.43.35-2.09V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                            </svg>
                            Continue with Google
                        </button>
                        )}
                    </>
                )}

                {!admin && (
                    <p className="text-center text-sm text-ink-500 mt-6">
                        New to Loomara? <Link to="/signup" className="text-sage-700 underline underline-offset-4" data-testid="link-signup">Create an account</Link>
                    </p>
                )}
                {admin && (
                    <p className="text-center text-xs text-ink-500 mt-6">
                        Not an admin? <Link to="/login" className="text-sage-700 underline underline-offset-4">Customer sign in</Link>
                    </p>
                )}
            </div>
        </div>
    );
}
